import { useState, useCallback, useEffect, useMemo } from 'react';
import { fetchResourceProgress, reportLessonProgress } from '@/service/api';

export interface UseLearningProgressOptions {
  /** 是否自动拉取进度，默认根据登录状态或开关决定 */
  enabled?: boolean;
}

/**
 * 学习资源与课时进度 Hook
 * 封装获取资源进度 fetchResourceProgress 与上报课时进度 reportLessonProgress
 *
 * @param resourceId 课程资源 ID 标识
 * @param options 配置项
 */
export function useLearningProgress(
  resourceId?: string | number,
  options: UseLearningProgressOptions = {}
) {
  const { enabled = true } = options;
  const [progressList, setProgressList] = useState<Api.Learning.ProgressResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 以 lesson_id 为 Key 的进度映射对象
  const progressMap = useMemo(() => {
    const map: Record<string, Api.Learning.ProgressResponse> = {};
    progressList.forEach((item) => {
      if (item.lesson_id) {
        map[item.lesson_id] = item;
      }
    });
    return map;
  }, [progressList]);

  // 拉取当前课程下所有课时的学习进度
  const fetchProgress = useCallback(async () => {
    if (!resourceId || !enabled) {
      setProgressList([]);
      setHasFetched(true);
      return [];
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchResourceProgress(resourceId);
      const list = Array.isArray(data) ? data : [];
      setProgressList(list);
      return list;
    } catch (err: any) {
      console.error('获取学习进度失败:', err);
      setError(err?.message || '获取学习进度失败');
      return [];
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [resourceId, enabled]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  // 上报指定课时的学习进度
  const reportProgress = useCallback(
    async (lessonId: string | number, data: Api.Learning.ProgressRequest) => {
      if (!lessonId) return;
      try {
        const updatedProgress = await reportLessonProgress(lessonId, data);
        if (updatedProgress) {
          setProgressList((prev) => {
            const targetIdStr = String(lessonId);
            const index = prev.findIndex((item) => String(item.lesson_id) === targetIdStr);
            if (index >= 0) {
              const next = [...prev];
              next[index] = { ...next[index], ...updatedProgress };
              return next;
            } else {
              return [...prev, updatedProgress];
            }
          });
        }
        return updatedProgress;
      } catch (err: any) {
        console.error('上报课时学习进度失败:', err);
      }
    },
    []
  );

  // 获取单个课时的进度信息
  const getLessonProgress = useCallback(
    (lessonId: string | number) => {
      return progressMap[String(lessonId)];
    },
    [progressMap]
  );

  return {
    progressList,
    progressMap,
    loading,
    hasFetched,
    error,
    fetchProgress,
    reportProgress,
    getLessonProgress,
  };
}
